import { StatTile } from '@bcecloud/portal-ui';
import { compactMoney, percent } from '@/lib/sales';
import { SALES_METRIC, changePct, type SalesMetric, type SalesSummary, type YoyPeriod } from '@/lib/sales-analytics';

const PERCENT_POINTS = 100;
const count = (value: number): string => value.toLocaleString('en-US');
const money = (value: number | null): string => (value === null ? 'n/a' : compactMoney(value));
const days = (value: number | null): string => (value === null ? 'n/a' : count(Math.round(value)));

/** The change between two figures that may be missing. */
function change(current: number | null, previous: number | null): number | null {
  return current === null || previous === null ? null : changePct(current, previous);
}

export interface SalesYoyStripProps {
  readonly periods: readonly YoyPeriod[];
}

/**
 * Are we up on last year? Month, quarter and year to date and the last 3, 6
 * and 12 months, each as of today against the same days a year earlier.
 * Independent of the range picked below.
 */
export function SalesYoyStrip({ periods }: SalesYoyStripProps): React.ReactElement {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-6">
      {periods.map((period) => (
        <StatTile
          key={period.key}
          label={period.label}
          value={compactMoney(period.booked)}
          changePct={changePct(period.booked, period.lastYearBooked)}
          hint={`vs ${compactMoney(period.lastYearBooked)} a year ago · ${count(period.bookings)} won (${count(period.lastYearBookings)})`}
        />
      ))}
    </div>
  );
}

export interface SalesSummaryTilesProps {
  readonly current: SalesSummary;
  readonly lastYear: SalesSummary;
  /** Open deals now × the range's levers; a stock, so it has no year-ago figure. */
  readonly velocityPerDay: number | null;
  readonly openDeals: number;
  /** The metric the chart shows; its tile reads as selected. */
  readonly metric: SalesMetric;
  readonly onSelectMetric: (metric: SalesMetric) => void;
}

/**
 * The range's sales-velocity levers (deals created, win rate, average deal,
 * days to sign), each against the same days last year, and the velocity
 * today's open deals would book at those levers.
 * A tile with a chart metric shows it below when picked.
 */
export function SalesSummaryTiles({ current, lastYear, velocityPerDay, openDeals, metric, onSelectMetric }: SalesSummaryTilesProps): React.ReactElement {
  const select = (key: SalesMetric) => ({ onSelect: () => onSelectMetric(key), selected: metric === key });
  const winRatePoints =
    current.winRate === null || lastYear.winRate === null ? null : (current.winRate - lastYear.winRate) * PERCENT_POINTS;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 xl:grid-cols-7">
      <StatTile
        label="Booked"
        value={compactMoney(current.booked)}
        changePct={changePct(current.booked, lastYear.booked)}
        hint={`${compactMoney(lastYear.booked)} last year`}
        {...select(SALES_METRIC.booked)}
      />
      <StatTile
        label="Deals won"
        value={count(current.bookings)}
        changePct={changePct(current.bookings, lastYear.bookings)}
        hint={`${count(lastYear.bookings)} last year`}
        {...select(SALES_METRIC.bookings)}
      />
      <StatTile
        label="Deals created"
        value={count(current.created)}
        changePct={changePct(current.created, lastYear.created)}
        hint={`${count(lastYear.created)} last year`}
        {...select(SALES_METRIC.created)}
      />
      <StatTile
        label="Win rate"
        value={percent(current.winRate)}
        changePct={winRatePoints}
        changeSuffix=" pts"
        hint={`${percent(lastYear.winRate)} last year`}
        {...select(SALES_METRIC.winRate)}
      />
      <StatTile
        label="Average deal"
        value={money(current.averageDeal)}
        changePct={change(current.averageDeal, lastYear.averageDeal)}
        hint={`${money(lastYear.averageDeal)} last year`}
        {...select(SALES_METRIC.averageDeal)}
      />
      <StatTile
        label="Days to sign"
        value={days(current.medianDaysToSign)}
        changePct={change(current.medianDaysToSign, lastYear.medianDaysToSign)}
        downIsGood
        hint={`Median · ${days(lastYear.medianDaysToSign)} last year`}
      />
      <StatTile
        label="Velocity per day"
        value={money(velocityPerDay)}
        hint={`${count(openDeals)} open × win rate × average deal ÷ days to sign`}
      />
    </div>
  );
}
